/**
 * Détection des données qu'un template de landing page attend du serveur.
 *
 * Le moteur (`app-landingpage`) met à disposition du template un objet plat, `rawData`, renvoyé par le
 * script Groovy de la configuration. Un template le lit de plusieurs façons, que ce module reconnaît
 * sans exécuter le template :
 * - les composants du moteur : `<dmp-display name="x">`, `<dmp-input field="x">`,
 *   `<dmp-select-airport name="x">`, `<dmp-address-form>`, `<dmp-display-newsletter>`… ;
 * - les champs natifs marqués `class="dmp-selector"`, préremplis d'après leur `name` ;
 * - les expressions `rawData?.x`, `services.restService.rawData.getValue()?.x`, `rawData['x']`, dans le
 *   HTML comme dans le TypeScript (y compris via une variable : `const d = ….getValue(); d.x`) ;
 * - les tableaux parcourus par `*ngFor="let p of rawData?.passagers"`, avec les champs lus sur `p` ;
 * - les clés numérotées construites par concaténation (`'passenger_' + (idx + 1) + '_email'`).
 *
 * C'est une analyse textuelle : une clé calculée autrement (fonction, boucle sur les clés) n'est pas vue.
 * Le mode JSON des données de test couvre ces cas.
 *
 * `<dmp-input name="x">` sans `field` n'est pas une donnée lue : le composant ne se préremplit que d'après
 * `field` (il recopie `name` dans son constructeur, avant que les entrées soient posées).
 */

export type TemplateValueType = 'text' | 'date' | 'number';

/** Une valeur simple : `rawData.email`. */
export interface TemplateValueVariable {
    kind: 'value';
    key: string;
    type: TemplateValueType;
    /** Valeurs auxquelles le template compare la clé, ou options d'un `<select>` : proposées à la saisie. */
    suggestions: string[];
}

/** Un tableau d'objets : `*ngFor="let p of rawData.passengers"`. */
export interface TemplateListVariable {
    kind: 'list';
    key: string;
    /** Champs lus sur chaque élément. */
    fields: string[];
}

export interface TemplateNumberedField {
    /** Nom de colonne : la partie propre à ce champ (« email » pour `passenger_{n}_email`). */
    label: string;
    prefix: string;
    suffix: string;
    type: TemplateValueType;
}

/** Des clés numérotées qui forment une liste : `passenger_1_email`, `passenger_2_email`… */
export interface TemplateNumberedVariable {
    kind: 'numbered';
    /** Nom du groupe, tel qu'écrit dans le template : `passenger_{n}_…`. */
    key: string;
    /** Premier numéro : 1 pour `(idx + 1)`, 0 pour un index brut. */
    start: number;
    fields: TemplateNumberedField[];
}

export type TemplateVariable = TemplateValueVariable | TemplateListVariable | TemplateNumberedVariable;

export interface TemplateAnalysis {
    /** Dans l'ordre de première apparition : HTML d'abord, puis TypeScript. */
    variables: TemplateVariable[];
    /** Étapes déclarées par `<dmp-step value="n">`, triées. */
    steps: number[];
}

/** Clé lue par une expression : littérale (`'email'`) ou numérotée (`'pax_' + i + '_nom'`). */
type KeyExpression =
    | { literal: string }
    | { prefix: string; suffix: string; placeholder: string };

/** Jeton qui remplace les accès à la valeur courante de `rawData` (`….rawData.getValue()`). */
const RAW = '__RAWDATA__';
const IDENT = '[A-Za-z_$][\\w$]*';
/** API du `BehaviorSubject` : `rawData.subscribe(…)` n'est pas la lecture d'une clé. */
const SUBJECT_MEMBERS = new Set(['subscribe', 'next', 'getValue', 'value', 'pipe', 'asObservable', 'complete', 'error']);
/** Balises sans fermeture : un `*ngFor` posé dessus ne couvre que la balise elle-même. */
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr']);
const MAX_SUGGESTIONS = 12;

/** Composants du moteur qui lisent des clés fixes, sans attribut pour les nommer. */
const ADDRESS_FORM_KEYS = ['country_of_residence', 'address', 'address2', 'zipcode', 'city'];
const NEWSLETTER_FIELDS = ['name', 'uuid', 'channelEnabled', 'statusSubEmail', 'statusSubSms'];

interface Collected {
    order: number;
    variable: TemplateVariable;
}

interface NumberedCollected {
    order: number;
    /** Un numéro calculé (`idx + 1`) a été vu : la liste part de 1. */
    oneBased: boolean;
    fields: TemplateNumberedField[];
}

export function analyzeTemplate(html: string, typescript: string): TemplateAnalysis {
    const collector = new Collector(stripTsComments(typescript ?? ''));
    const htmlSource = stripHtmlComments(html ?? '');
    const tsOffset = htmlSource.length + 1;

    const steps = scanTags(htmlSource, collector);
    scanExpressions(normalize(htmlSource), 0, collector, []);
    const ts = normalize(collector.ts);
    scanExpressions(ts, tsOffset, collector, findAliases(ts));
    scanDateHelpers(ts, tsOffset, collector);

    return {variables: collector.result(), steps};
}

/** Nom affiché d'une clé numérotée : `passenger_{n}_email`. */
export function numberedKey(field: TemplateNumberedField, n: number | string): string {
    return `${field.prefix}${n}${field.suffix}`;
}

class Collector {
    private readonly values = new Map<string, Collected>();
    private readonly numbered = new Map<string, NumberedCollected>();

    constructor(readonly ts: string) {
    }

    value(key: string, order: number, type: TemplateValueType = 'text', suggestions: string[] = []) {
        if (!key) {
            return;
        }
        const existing = this.values.get(key);
        if (!existing) {
            this.values.set(key, {order, variable: {kind: 'value', key, type, suggestions: [...new Set(suggestions)]}});
            return;
        }
        existing.order = Math.min(existing.order, order);
        const variable = existing.variable;
        if (variable.kind === 'value') {
            // Un indice de type (date, nombre) l'emporte sur le texte par défaut.
            if (variable.type === 'text') {
                variable.type = type;
            }
            for (const suggestion of suggestions) {
                if (!variable.suggestions.includes(suggestion) && variable.suggestions.length < MAX_SUGGESTIONS) {
                    variable.suggestions.push(suggestion);
                }
            }
        }
    }

    list(key: string, order: number, fields: string[]) {
        const existing = this.values.get(key);
        if (existing?.variable.kind === 'list') {
            existing.order = Math.min(existing.order, order);
            existing.variable.fields = [...new Set([...existing.variable.fields, ...fields])];
            return;
        }
        // Une clé parcourue par `*ngFor` est un tableau, même si elle est aussi lue ailleurs
        // (`rawData.passengers?.length`) : le tableau l'emporte.
        this.values.set(key, {
            order: Math.min(existing?.order ?? order, order),
            variable: {kind: 'list', key, fields: [...new Set(fields)]}
        });
    }

    key(expression: KeyExpression | null, order: number, type: TemplateValueType = 'text', suggestions: string[] = []) {
        if (!expression) {
            return;
        }
        if ('literal' in expression) {
            this.value(expression.literal, order, type, suggestions);
            return;
        }
        const {prefix, suffix, placeholder} = expression;
        const group = this.numbered.get(prefix) ?? {order, oneBased: false, fields: []};
        group.order = Math.min(group.order, order);
        // `idx` seul est un index qui part de 0 ; `(idx + 1)`, un numéro qui part de 1. Le script peut
        // recevoir ce numéro en paramètre (`pax_${n}`) : dès qu'un calcul est vu, la liste part de 1.
        if (!/^[\w$]+$/.test(stripParentheses(placeholder))) {
            group.oneBased = true;
        }
        const field = group.fields.find(f => f.suffix === suffix);
        if (field) {
            if (field.type === 'text') {
                field.type = type;
            }
        } else {
            group.fields.push({label: numberedLabel(prefix, suffix), prefix, suffix, type});
        }
        this.numbered.set(prefix, group);
    }

    result(): TemplateVariable[] {
        const numbered: Collected[] = [...this.numbered.entries()].map(([prefix, group]) => ({
            order: group.order,
            variable: {
                kind: 'numbered',
                key: group.fields.length === 1 ? numberedKey(group.fields[0], '{n}') : `${prefix}{n}…`,
                start: group.oneBased ? 1 : 0,
                fields: group.fields
            }
        }));
        return [...this.values.values(), ...numbered]
            .sort((a, b) => a.order - b.order)
            .map(c => c.variable);
    }
}

function numberedLabel(prefix: string, suffix: string): string {
    const label = suffix.replace(/^[^A-Za-z0-9]+/, '') || prefix.replace(/[^A-Za-z0-9]+$/, '');
    return label || prefix + suffix;
}

/* ---------------------------------------------------------------------------------------------------
 * Balises : composants du moteur, champs `dmp-selector`, étapes et boucles `*ngFor`.
 * ------------------------------------------------------------------------------------------------- */

const TAG_REGEX = /<([a-zA-Z][\w-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;
const ATTR_REGEX = /([^\s=/>"']+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>"']+)))?/g;

function scanTags(html: string, collector: Collector): number[] {
    const steps = new Set<number>();
    for (const match of allMatches(html, TAG_REGEX)) {
        const tag = match[1].toLowerCase();
        const order = match.index;
        const attrs = parseAttributes(match[2]);

        switch (tag) {
            case 'dmp-display':
                readKeyAttribute(attrs, 'name', order, collector, attrs.type === 'date' ? 'date' : 'text');
                break;
            case 'dmp-input':
                readKeyAttribute(attrs, 'field', order, collector, inputType(attrs.type));
                break;
            case 'dmp-select-airport':
                readKeyAttribute(attrs, 'name', order, collector);
                break;
            case 'dmp-address-form':
                ADDRESS_FORM_KEYS.forEach(key => collector.value(key, order));
                break;
            case 'dmp-display-newsletter':
                collector.list('newsletters', order, NEWSLETTER_FIELDS);
                break;
            case 'dmp-image-affiliation':
            case 'dmp-emailing-template':
                // Sans attribut, le composant prend l'affilié et la langue de la réservation.
                if (!('iata' in attrs) && !('[iata]' in attrs)) {
                    collector.value('affiliate_id', order);
                }
                if (!('lang' in attrs) && !('[lang]' in attrs)) {
                    collector.value('booking_language', order, 'text', ['FR', 'EN']);
                }
                break;
            case 'dmp-step': {
                const step = Number(attrs.value);
                if (Number.isInteger(step)) {
                    steps.add(step);
                }
                break;
            }
        }

        if (attrs.name && /(^|\s)dmp-selector(\s|$)/.test(attrs.class ?? '')) {
            const suggestions = tag === 'select' ? selectOptions(html, match.index + match[0].length) : [];
            collector.value(attrs.name, order, tag === 'input' ? inputType(attrs.type) : 'text', suggestions);
        }

        if (attrs['*ngfor']) {
            scanLoop(html, match, tag, attrs['*ngfor'], collector);
        }
    }
    return [...steps].sort((a, b) => a - b);
}

/** Attributs d'une balise, noms en minuscules (`*ngFor` → `*ngfor`, `[name]` reste `[name]`). */
function parseAttributes(source: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    for (const match of allMatches(source, ATTR_REGEX)) {
        attrs[match[1].toLowerCase()] = match[2] ?? match[3] ?? match[4] ?? '';
    }
    return attrs;
}

/** `name="x"` ou `[name]="'pax_' + i"` : la clé est la valeur de l'attribut, ou son expression. */
function readKeyAttribute(attrs: Record<string, string>, name: string, order: number, collector: Collector,
                          type: TemplateValueType = 'text') {
    if (attrs[name]) {
        collector.value(attrs[name], order, type);
    } else if (attrs[`[${name}]`]) {
        collector.key(parseKeyExpression(attrs[`[${name}]`], collector.ts), order, type);
    }
}

function inputType(type: string | undefined): TemplateValueType {
    switch (type) {
        case 'date':
        case 'datetime-local':
            return 'date';
        case 'number':
            return 'number';
        default:
            return 'text';
    }
}

function selectOptions(html: string, from: number): string[] {
    const end = html.indexOf('</select', from);
    const body = html.slice(from, end < 0 ? undefined : end);
    return allMatches(body, /<option\b[^>]*\bvalue\s*=\s*(?:"([^"]*)"|'([^']*)')/gi)
        .map(m => m[1] ?? m[2])
        .filter(v => v && !v.includes('{{'))
        .slice(0, MAX_SUGGESTIONS);
}

/** `*ngFor="let p of rawData?.passengers"` : le tableau, puis les champs lus sur `p` dans l'élément. */
function scanLoop(html: string, match: RegExpExecArray, tag: string, expression: string, collector: Collector) {
    const loop = /^\s*let\s+([A-Za-z_$][\w$]*)\s+of\s+([^;]+)/.exec(expression);
    if (!loop) {
        return;
    }
    const [, variable, source] = loop;
    const key = rootKey(normalize(source.trim()));
    if (!key) {
        return;
    }
    const start = match.index;
    const end = elementEnd(html, tag, start + match[0].length, match[0].endsWith('/>'));
    const scope = normalize(html.slice(start, end));
    const fields = [...accessedKeys(scope, variable, collector.ts)].map(access => access.key).filter(Boolean);
    collector.list(key, start, fields);
}

/** `rawData?.x`, `rawData['x']` ou le jeton de `getValue()` suivi d'une clé : la clé, sinon `null`. */
function rootKey(expression: string): string | null {
    const root = `(?:${RAW}|rawData)`;
    const dot = new RegExp(`^${root}\\s*(?:\\?\\.|\\.)\\s*(${IDENT})\\s*$`).exec(expression);
    if (dot) {
        return dot[1];
    }
    const bracket = new RegExp(`^${root}\\s*(?:\\?\\.)?\\s*\\[\\s*(['"])([^'"]+)\\1\\s*\\]\\s*$`).exec(expression);
    return bracket ? bracket[2] : null;
}

/** Fin de l'élément ouvert en `from` : on compte les balises de même nom jusqu'à sa fermeture. */
function elementEnd(html: string, tag: string, from: number, selfClosing: boolean): number {
    if (selfClosing || VOID_TAGS.has(tag)) {
        return from;
    }
    const regex = new RegExp(`<(/?)${tag}\\b((?:[^>"']|"[^"]*"|'[^']*')*)>`, 'gi');
    regex.lastIndex = from;
    let depth = 1;
    for (let m = regex.exec(html); m; m = regex.exec(html)) {
        if (m[1]) {
            depth--;
        } else if (!m[2].trim().endsWith('/')) {
            depth++;
        }
        if (depth === 0) {
            return m.index + m[0].length;
        }
    }
    return html.length;
}

/* ---------------------------------------------------------------------------------------------------
 * Expressions : `rawData.x`, `….getValue()?.x`, `alias['x']`, et les indices de type autour.
 * ------------------------------------------------------------------------------------------------- */

interface Alias {
    name: string;
    start: number;
    end: number;
}

interface Access {
    key?: string;
    expression?: KeyExpression;
    index: number;
    end: number;
}

/** Remplace les lectures de la valeur courante (`services.restService.rawData.getValue()`) par un jeton. */
function normalize(text: string): string {
    return text.replace(/(?:[\w$]+\s*\??\.\s*)*rawData\s*\??\.\s*(?:getValue\s*\(\s*\)|value\b)/g, RAW);
}

/**
 * Variables qui désignent les données dans le TypeScript : `const data = ….getValue();` (jusqu'à la fin du
 * script) et le paramètre de `rawData.subscribe(data => { … })` (dans le corps du rappel).
 */
function findAliases(ts: string): Alias[] {
    const aliases: Alias[] = [];
    for (const m of allMatches(ts, new RegExp(`(?:const|let|var)\\s+(${IDENT})\\s*=\\s*${RAW}\\s*[;\\n]`, 'g'))) {
        aliases.push({name: m[1], start: m.index, end: ts.length});
    }
    const subscribe = new RegExp(`rawData\\s*\\.\\s*subscribe\\s*\\(\\s*(?:\\(\\s*(${IDENT})[^)]*\\)|(${IDENT}))\\s*=>\\s*`, 'g');
    for (const m of allMatches(ts, subscribe)) {
        const bodyStart = m.index + m[0].length;
        const end = ts[bodyStart] === '{' ? balancedEnd(ts, bodyStart, '{', '}') : ts.indexOf(')', bodyStart);
        aliases.push({name: m[1] ?? m[2], start: bodyStart, end: end < 0 ? ts.length : end});
    }
    return aliases;
}

function scanExpressions(text: string, offset: number, collector: Collector, aliases: Alias[]) {
    const roots = new Set([RAW, 'rawData', ...aliases.map(a => a.name)]);
    for (const root of roots) {
        const scopes = aliases.filter(a => a.name === root);
        for (const access of accessedKeys(text, root, collector.ts)) {
            if (scopes.length && !scopes.some(s => access.index >= s.start && access.index <= s.end)) {
                continue;
            }
            if (root === 'rawData' && access.key && SUBJECT_MEMBERS.has(access.key)) {
                continue;
            }
            const hints = typeHints(text, access);
            collector.key(access.expression ?? {literal: access.key}, offset + access.index, hints.type, hints.suggestions);
        }
    }
}

/**
 * Clés lues sur `root` : `root.x`, `root?.x`, `root['x']`, `root?.[expr]` (appels de méthode exclus).
 * `ts` sert à résoudre une constante entre crochets (`root[champ]` avec `const champ = 'x'`).
 */
function* accessedKeys(text: string, root: string, ts: string): Generator<Access> {
    const escaped = root.replace(/\$/g, '\\$');
    // `(?![\w$])` avant l'exclusion des appels : sans lui, `hasOwnProperty(` se lirait `hasOwnPropert`.
    const regex = new RegExp(`(?<![\\w$.])${escaped}\\s*(?:(\\?\\.|\\.)\\s*(${IDENT})(?![\\w$])(?!\\s*\\()|(?:\\?\\.)?\\s*\\[)`, 'g');
    for (const m of allMatches(text, regex)) {
        if (m[2]) {
            yield {key: m[2], index: m.index, end: m.index + m[0].length};
            continue;
        }
        const open = m.index + m[0].length - 1;
        const close = balancedEnd(text, open, '[', ']');
        if (close < 0) {
            continue;
        }
        const expression = parseKeyExpression(text.slice(open + 1, close - 1), ts);
        if (expression) {
            yield {expression, key: 'literal' in expression ? expression.literal : undefined, index: m.index, end: close};
        }
    }
}

/** Ce que le code fait de la valeur lue : comparée à un texte, à un nombre, ou prise comme taille. */
function typeHints(text: string, access: Access): { type: TemplateValueType; suggestions: string[] } {
    const after = text.slice(access.end, access.end + 120);
    const before = text.slice(Math.max(0, access.index - 40), access.index);
    const compared = /^\s*(?:===?|!==?)\s*(['"])([^'"]*)\1/.exec(after);
    if (compared) {
        return {type: 'text', suggestions: compared[2] ? [compared[2]] : []};
    }
    if (/^\s*(?:[<>]=?)\s*-?\d/.test(after) || /constructor\s*\(\s*$/.test(before)) {
        return {type: 'number', suggestions: []};
    }
    return {type: 'text', suggestions: []};
}

/** `services.date.greaterThan('birthdate', 18)` : la clé est une date. */
function scanDateHelpers(ts: string, offset: number, collector: Collector) {
    for (const m of allMatches(ts, /\bdate\s*\.\s*(?:greaterThan|lessThan)\s*\(\s*([^,)]+)/g)) {
        collector.key(parseKeyExpression(m[1], collector.ts), offset + m.index, 'date');
    }
}

/**
 * Clé désignée par une expression : un texte (`'email'`), une concaténation avec une seule partie
 * variable (`'pax_' + (i + 1) + '_nom'`), un gabarit (`` `pax_${i}_nom` ``), ou une constante du script
 * qui vaut l'un des trois. Au-delà (deux parties variables, appel de fonction), `null`.
 */
export function parseKeyExpression(source: string, ts: string): KeyExpression | null {
    const expression = stripParentheses(source.trim());
    const template = /^`([^`$]*)\$\{([^}]*)\}([^`$]*)`$/.exec(expression);
    if (template) {
        return {prefix: template[1], suffix: template[3], placeholder: template[2].trim()};
    }
    const literalOnly = /^`([^`$]*)`$/.exec(expression);
    if (literalOnly) {
        return {literal: literalOnly[1]};
    }

    const parts = splitConcatenation(expression);
    const literals = parts.map(p => /^(['"])(.*)\1$/.exec(p.trim())?.[2]);
    const variable = literals.map((l, i) => l === undefined ? i : -1).filter(i => i >= 0);

    if (!variable.length) {
        return {literal: literals.join('')};
    }
    if (parts.length === 1 && ts && new RegExp(`^${IDENT}$`).test(expression)) {
        const declaration = new RegExp(`(?:const|let|var)\\s+${expression.replace(/\$/g, '\\$')}\\s*=\\s*([^;\\n]+)`).exec(ts);
        return declaration ? parseKeyExpression(declaration[1], '') : null;
    }
    // Une seule partie variable, d'un seul tenant, et du texte autour.
    const first = variable[0];
    const last = variable[variable.length - 1];
    if (last - first + 1 !== variable.length || variable.length === parts.length) {
        return null;
    }
    return {
        prefix: literals.slice(0, first).join(''),
        suffix: literals.slice(last + 1).join(''),
        placeholder: parts.slice(first, last + 1).join('+').trim()
    };
}

/** Découpe `a + (b + 1) + 'c'` sur les `+` de premier niveau, hors chaînes et parenthèses. */
function splitConcatenation(expression: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let quote: string | null = null;
    let current = '';
    for (const char of expression) {
        if (quote) {
            if (char === quote) {
                quote = null;
            }
        } else if (char === '\'' || char === '"' || char === '`') {
            quote = char;
        } else if ('([{'.includes(char)) {
            depth++;
        } else if (')]}'.includes(char)) {
            depth--;
        } else if (char === '+' && depth === 0) {
            parts.push(current);
            current = '';
            continue;
        }
        current += char;
    }
    parts.push(current);
    return parts.map(p => p.trim()).filter(p => p.length);
}

function stripParentheses(expression: string): string {
    let result = expression;
    while (result.startsWith('(') && result.endsWith(')') && balancedEnd(result, 0, '(', ')') === result.length) {
        result = result.slice(1, -1).trim();
    }
    return result;
}

/** Position qui suit le caractère fermant apparié à celui en `start`, ou -1. Ignore les chaînes. */
function balancedEnd(text: string, start: number, open: string, close: string): number {
    let depth = 0;
    let quote: string | null = null;
    for (let i = start; i < text.length; i++) {
        const char = text[i];
        if (quote) {
            if (char === '\\') {
                i++;
            } else if (char === quote) {
                quote = null;
            }
        } else if (char === '\'' || char === '"' || char === '`') {
            quote = char;
        } else if (char === open) {
            depth++;
        } else if (char === close && --depth === 0) {
            return i + 1;
        }
    }
    return -1;
}

/**
 * Équivalent de `String.prototype.matchAll`, absent de la `lib` TypeScript du projet. `regex` porte le
 * drapeau `g` ; toutes les correspondances sont lues avant d'être rendues, l'expression peut donc être
 * partagée (constantes du module).
 */
function allMatches(text: string, regex: RegExp): RegExpExecArray[] {
    const matches: RegExpExecArray[] = [];
    regex.lastIndex = 0;
    for (let m = regex.exec(text); m; m = regex.exec(text)) {
        matches.push(m);
        if (m[0] === '') {
            regex.lastIndex++;
        }
    }
    return matches;
}

/* ---------------------------------------------------------------------------------------------------
 * Commentaires : remplacés par des espaces, pour garder les positions (ordre d'apparition).
 * ------------------------------------------------------------------------------------------------- */

function stripHtmlComments(html: string): string {
    return html.replace(/<!--[\s\S]*?-->/g, c => c.replace(/[^\n]/g, ' '));
}

/** Retire `// …` et `/* … *\/` du TypeScript, sans toucher aux chaînes (`'https://…'`). */
function stripTsComments(ts: string): string {
    let result = '';
    let quote: string | null = null;
    for (let i = 0; i < ts.length; i++) {
        const char = ts[i];
        if (quote) {
            result += char;
            if (char === '\\' && i + 1 < ts.length) {
                result += ts[++i];
            } else if (char === quote) {
                quote = null;
            }
        } else if (char === '\'' || char === '"' || char === '`') {
            quote = char;
            result += char;
        } else if (char === '/' && ts[i + 1] === '/') {
            const end = ts.indexOf('\n', i);
            const stop = end < 0 ? ts.length : end;
            result += ' '.repeat(stop - i);
            i = stop - 1;
        } else if (char === '/' && ts[i + 1] === '*') {
            const end = ts.indexOf('*/', i + 2);
            const stop = end < 0 ? ts.length : end + 2;
            result += ts.slice(i, stop).replace(/[^\n]/g, ' ');
            i = stop - 1;
        } else {
            result += char;
        }
    }
    return result;
}
